import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { catchError, switchMap, tap, timeout, delay, exhaustMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectUserDetails } from '../../store/user/auth/user-auth.selectors';
import { Api } from '../../api/actuator/api';
import { health } from '../../api/actuator/fn/actuator/health';
import { HealthResponse } from '../../api/actuator/models/health-response';
import { StrictHttpResponse } from '../../api/actuator/strict-http-response';

export enum HealthStatus {
  UP = 'UP',
  DOWN = 'DOWN',
  ERROR = 'ERROR',
}

@Injectable({
  providedIn: 'root',
})
export class HealthMonitoringService implements OnDestroy {
  private readonly POLLING_INTERVAL = 10000; // 10 seconds
  private readonly MAX_RETRIES = 3;
  private readonly TIMEOUT_DURATION = 10000; // 10 seconds
  private readonly JITTER_FACTOR = 0.2; // 20% jitter

  private statusSubject = new BehaviorSubject<HealthStatus>(HealthStatus.UP);
  public status$ = this.statusSubject.asObservable();

  private isBackendDownSubject = new BehaviorSubject<boolean>(false);
  public isBackendDown$ = this.isBackendDownSubject.asObservable();

  private isReconnectingSubject = new BehaviorSubject<boolean>(false);
  public isReconnecting$ = this.isReconnectingSubject.asObservable();

  private pollingSubscription?: Subscription;
  private retryCounter = 0;

  constructor(
    private api: Api,
    private http: HttpClient,
    private store: Store,
    private ngZone: NgZone
  ) {
    this.initAuthListener();
  }

  private initAuthListener() {
    this.store.select(selectUserDetails).subscribe((user) => {
      if (user) {
        if (!this.pollingSubscription) {
          this.startPolling();
        }
      } else {
        this.stopPolling();
      }
    });
  }

  private stopPolling() {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.retryCounter = 0;
    this.isBackendDownSubject.next(false);
    this.isReconnectingSubject.next(false);
  }

  private startPolling() {
  this.ngZone.runOutsideAngular(() => {
    this.pollingSubscription = timer(
      0,
      this.POLLING_INTERVAL + this.getJitter(this.POLLING_INTERVAL)
    )
      .pipe(
        exhaustMap(() => this.checkHealth())
      )
      .subscribe();
  });
}

  private checkHealth(): Observable<StrictHttpResponse<HealthResponse>> {
    return health(this.http, this.api.rootUrl, {}).pipe(
      timeout(this.TIMEOUT_DURATION),
      tap((response: StrictHttpResponse<HealthResponse>) => {
        const status = response.body.status === 'UP' ? HealthStatus.UP : HealthStatus.DOWN;
        this.handleResponse(status);
        this.retryCounter = 0;
      }),
      catchError((error: HttpErrorResponse | Error) => {
        this.handleError(error);
        return this.initiateRetry();
      })
    );
  }

  private handleResponse(status: HealthStatus) {
  this.ngZone.run(() => {
    this.statusSubject.next(status);

    if (status === HealthStatus.UP) {
      if (this.isBackendDownSubject.value) {
        this.isReconnectingSubject.next(true);

        timer(2000).subscribe(() => {
          this.ngZone.run(() => {
            this.isBackendDownSubject.next(false);
            this.isReconnectingSubject.next(false);
          });
        });
      } else {
        this.isBackendDownSubject.next(false);
      }
    } else {
      this.retryCounter++;
      this.checkRetryLimit();
    }
  });
}

  private handleError(error: HttpErrorResponse | Error) {
  this.ngZone.run(() => {
    this.statusSubject.next(HealthStatus.ERROR);
    this.retryCounter++;
    this.checkRetryLimit();
  });
}

  private checkRetryLimit() {
  if (this.retryCounter >= this.MAX_RETRIES) {
    this.ngZone.run(() => {
      this.isBackendDownSubject.next(true);
    });
  }
}

  private initiateRetry(): Observable<StrictHttpResponse<HealthResponse>> {
    const delayTime = Math.pow(2, this.retryCounter) * 1000; // Exponential backoff starting from 1s (2^0 is handled as 2^count)
    // Actually starting from 1s: retryCounter 1 -> 1s, 2 -> 2s, 3 -> 4s
    const jitteredDelay = delayTime + this.getJitter(delayTime);
    return timer(jitteredDelay).pipe(switchMap(() => this.checkHealth()));
  }

  private getJitter(base: number): number {
    return (Math.random() * 2 - 1) * base * this.JITTER_FACTOR;
  }

  ngOnDestroy() {
    this.pollingSubscription?.unsubscribe();
  }
}
